/**
 * Timeline event filtering logic
 */

import { convertToTimelineEvent, assignEventToLane, eventMatchesPassage, type TimelineEvent } from './timeline-utils';
import { resolveDisplayDate } from '@/lib/date-utils';
import { NON_ANE_EVENT_TITLES } from '@/lib/constants/world-events';


export type RelevanceMode = 'passage' | 'time-period';

interface FilterTimelineEventsParams {
    passageEvents: HistoricalEventByPassage[] | undefined;
    bookEvents: HistoricalEventByBook[] | undefined;
    referenceEvents: HistoricalEvent[];
    relevanceMode: RelevanceMode;
    passage: Passage | undefined;
    isLaneVisible: (laneId: string) => boolean;
    historicalEventsByBook?: HistoricalEventsByBook | null;
    preferredChronology?: BiblicalChronology;
    showWorldEvents?: boolean;
}


/**
 * Filter and convert events to timeline format based on relevance mode and lane visibility
 */
export function filterTimelineEvents({
    passageEvents,
    bookEvents,
    referenceEvents,
    relevanceMode,
    passage,
    isLaneVisible,
    historicalEventsByBook,
    preferredChronology,
    showWorldEvents = false
}: FilterTimelineEventsParams): TimelineEvent[] {

    const events: TimelineEvent[] = [];

    // Add book composition event from HistoricalEventsByBook
    if (historicalEventsByBook?.composition && isLaneVisible('biblical-composition')) {
        const { activeDate: date, alternatives } = resolveDisplayDate(
            historicalEventsByBook.composition.compositionDate,
            historicalEventsByBook.composition.compositionDateAlternatives,
            preferredChronology
        );

        const compositionEvent: TimelineEvent = {
            title: historicalEventsByBook.composition.title,
            summary: historicalEventsByBook.composition.summary,
            description: historicalEventsByBook.composition.description,
            relevance: historicalEventsByBook.composition.relevance,
            displayDateRange: date.displayDate,
            sortableYearStart: date.startYear,
            sortableYearEnd: date.endYear || date.startYear,
            date,
            categories: [{ category: 'biblical-composition' }],
            importance: historicalEventsByBook.composition.importance,
            sources: historicalEventsByBook.composition.sources,
            keyFigures: historicalEventsByBook.composition.keyFigures,
            laneId: 'biblical-composition',
            source: 'book',
            alternativeDates: alternatives,
            region: historicalEventsByBook.composition.region,
            empire: historicalEventsByBook.composition.empire
        };
        events.push(compositionEvent);
    }


    // Add narrative setting event from HistoricalEventsByBook (if applicable)
    if (historicalEventsByBook?.narrativeSetting && isLaneVisible('biblical-narrative')) {
        const { activeDate: date, alternatives } = resolveDisplayDate(
            historicalEventsByBook.narrativeSetting.narrativeSettingDate,
            historicalEventsByBook.narrativeSetting.narrativeSettingDateAlternatives,
            preferredChronology
        );

        const narrativeEvent: TimelineEvent = {
            title: historicalEventsByBook.narrativeSetting.title,
            summary: historicalEventsByBook.narrativeSetting.summary,
            description: historicalEventsByBook.narrativeSetting.description,
            relevance: historicalEventsByBook.narrativeSetting.relevance,
            displayDateRange: date.displayDate,
            sortableYearStart: date.startYear,
            sortableYearEnd: date.endYear || date.startYear,
            date,
            categories: [{ category: 'biblical-narrative' }],
            importance: historicalEventsByBook.narrativeSetting.importance,
            sources: historicalEventsByBook.narrativeSetting.sources,
            keyFigures: historicalEventsByBook.narrativeSetting.keyFigures,
            laneId: 'biblical-narrative',
            source: 'book',
            alternativeDates: alternatives,
            region: historicalEventsByBook.narrativeSetting.region,
            empire: historicalEventsByBook.narrativeSetting.empire
        };
        events.push(narrativeEvent);
    }


    // Always add passage events (filtered by swimlane)
    // These are events that happened IN the biblical narrative for this specific passage
    if (passageEvents) {
        for (const event of passageEvents) {
            const laneId = assignEventToLane(event);
            if (isLaneVisible(laneId)) {
                events.push(convertToTimelineEvent(event, 'passage', preferredChronology));
            }
        }
    }

    // Always add book events (filtered by swimlane)
    // These are events that happened IN the biblical narrative for this book
    if (bookEvents) {
        for (const event of bookEvents) {
            const laneId = assignEventToLane(event);
            if (isLaneVisible(laneId)) {
                events.push(convertToTimelineEvent(event, 'book', preferredChronology));
            }
        }
    }


    // Add reference events based on relevance mode
    // These are general historical/cultural events that provide CONTEXT
    if (referenceEvents.length > 0) {
        // Filter out non-ANE (world) events if not enabled
        const filteredReferenceEvents = showWorldEvents
            ? referenceEvents
            : referenceEvents.filter(e => !NON_ANE_EVENT_TITLES.includes(e.title));

        if (relevanceMode === 'passage') {
            // PASSAGE RELEVANCE MODE: Show events that reference this passage
            // Pre-filter events with biblicalReferences before checking passage match
            for (const event of filteredReferenceEvents) {
                if (event.biblicalReferences && event.biblicalReferences.length > 0 && passage && eventMatchesPassage(event, passage)) {
                    const laneId = assignEventToLane(event);
                    if (isLaneVisible(laneId)) {
                        events.push(convertToTimelineEvent(event, 'reference', preferredChronology));
                    }
                }
            }
        } else if (relevanceMode === 'time-period') {
            // TIME PERIOD CONTEXT MODE: Show all events (no date filtering)
            for (const event of filteredReferenceEvents) {
                const laneId = assignEventToLane(event);
                if (isLaneVisible(laneId)) {
                    events.push(convertToTimelineEvent(event, 'reference', preferredChronology));
                }
            }
        }
    }

    return events;
}

